/**
 * Lazy loader for the Google Maps JavaScript SDK.
 *
 * The SDK is loaded on demand — only screens that actually show a map or an
 * address autocomplete pay for it, and it is never fetched at all when no API
 * key is configured. The script tag is injected once and shared: concurrent
 * callers all await the same promise.
 *
 * Requires VITE_GOOGLE_MAPS_API_KEY with these APIs enabled:
 *   - Maps JavaScript API  (the map in the visit details popup)
 *   - Places API (New)     (address autocomplete on the client form)
 *
 * Address suggestions use the Autocomplete Data API (AutocompleteSuggestion),
 * not the legacy `places.Autocomplete` widget — the legacy service is no longer
 * enabled for projects created after March 2025 and returns "You're calling a
 * legacy API, which is not enabled for your project". Fetching predictions
 * ourselves also means both address forms keep their own styling.
 */
const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Libraries we need beyond the core SDK. `places` powers the Autocomplete.
const LIBRARIES = 'places';

const CALLBACK_NAME = '__servepatchMapsReady';
const SCRIPT_ID = 'servepatch-google-maps';

let loaderPromise = null;

/** True when a key is configured — callers use this to hide map UI entirely. */
export const isMapsConfigured = () => Boolean(API_KEY);

/**
 * Resolve once the Maps SDK is ready, or reject if it can't be loaded.
 * Safe to call repeatedly — the script is only ever injected once.
 */
export const loadGoogleMaps = () => {
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (loaderPromise) return loaderPromise;

  if (!API_KEY) {
    return Promise.reject(new Error('VITE_GOOGLE_MAPS_API_KEY is not set'));
  }

  loaderPromise = new Promise((resolve, reject) => {
    // Google calls this global once the SDK has finished initialising.
    window[CALLBACK_NAME] = () => {
      delete window[CALLBACK_NAME];
      resolve(window.google.maps);
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}`
      + `&libraries=${LIBRARIES}&loading=async&callback=${CALLBACK_NAME}`;
    script.onerror = () => {
      // Let a later call retry — a failed load shouldn't poison the module.
      loaderPromise = null;
      script.remove();
      reject(new Error('Failed to load the Google Maps SDK'));
    };

    document.head.appendChild(script);
  });

  return loaderPromise;
};

/**
 * Start a new autocomplete billing session.
 *
 * Google bills a "session" as the keystrokes leading up to one place selection.
 * Reuse the same token across every keystroke, then throw it away and mint a
 * fresh one once a suggestion is picked.
 */
export const newSessionToken = async () => {
  // Must load the SDK first — this is the earliest thing the field calls, so
  // touching window.google before the script tag exists would throw and the
  // SDK would never get injected at all.
  await loadGoogleMaps();
  const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
  return new AutocompleteSessionToken();
};

// Address-ish place types. Restricting these keeps shops and landmarks out of
// the suggestions for a field that is meant to hold somebody's home.
const ADDRESS_TYPES = ['street_address', 'premise', 'subpremise', 'route'];

/**
 * Fetch address predictions for what the user has typed so far.
 * Resolves to `[{ id, primary, secondary, prediction }]` — `prediction` is
 * handed back to `resolvePlace` once the user picks one.
 *
 * `regionCodes` are ISO 3166-1 alpha-2 (note: the UK is 'gb', not 'uk').
 */
export const fetchAddressSuggestions = async (input, { sessionToken, regionCodes } = {}) => {
  const text = (input || '').trim();
  if (text.length < 3) return [];

  await loadGoogleMaps();
  const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');

  const base = {
    input: text,
    sessionToken,
    ...(regionCodes?.length ? { includedRegionCodes: regionCodes } : {}),
  };

  let suggestions;
  try {
    ({ suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      ...base,
      includedPrimaryTypes: ADDRESS_TYPES,
    }));
  } catch (err) {
    // Google rejects the whole request if it dislikes any one type filter.
    // Losing the filter only means noisier results — far better than a field
    // that silently stops suggesting anything.
    console.warn('[maps] address type filter rejected, retrying unfiltered:', err?.message || err);
    ({ suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(base));
  }

  return (suggestions || [])
    .map((s) => s.placePrediction)
    .filter(Boolean)
    .map((prediction) => ({
      id: prediction.placeId,
      primary: prediction.mainText?.toString() || prediction.text?.toString() || '',
      secondary: prediction.secondaryText?.toString() || '',
      prediction,
    }));
};

/**
 * Turn a chosen prediction into the address fields we store.
 * This is the billable "place details" step, so it only runs on selection.
 */
export const resolvePlace = async (prediction) => {
  const place = prediction.toPlace();
  await place.fetchFields({
    fields: ['addressComponents', 'location', 'formattedAddress'],
  });
  return parsePlace(place);
};

/**
 * Pull our address fields out of a Places (New) `addressComponents` array.
 * Returns the subset we can fill; anything Google didn't supply is omitted so
 * we never blank out a value the user typed themselves.
 */
export const parsePlace = (place) => {
  const comps = place?.addressComponents || [];
  const get = (type, short = false) => {
    const hit = comps.find((c) => c.types.includes(type));
    if (!hit) return '';
    return short ? hit.shortText : hit.longText;
  };

  // UK addresses put the house number in street_number and the street in route;
  // "premise" covers named buildings that have no number.
  const streetNumber = get('street_number') || get('premise');
  const route = get('route');
  const line1 = [streetNumber, route].filter(Boolean).join(' ');

  // Google has no single "address line 2" — the sub-locality / neighbourhood is
  // the closest equivalent and is what UK users expect to see there.
  const line2 = get('sublocality_level_1') || get('neighborhood') || '';

  const city = get('postal_town') || get('locality') || get('administrative_area_level_2');
  const county = get('administrative_area_level_2') || get('administrative_area_level_1');

  const out = {};
  if (line1) out.address_line_1 = line1;
  if (line2) out.address_line_2 = line2;
  if (city) out.city = city;
  // Don't repeat the town in the county box when Google gives the same value.
  if (county && county !== city) out.county_state = county;
  if (get('postal_code')) out.postcode = get('postal_code');
  if (get('country')) out.country = get('country');

  const loc = place?.location;
  if (loc) {
    out.latitude = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
    out.longitude = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
  }

  // Fall back to the formatted address when we couldn't build a street line —
  // better than leaving the field the user was typing in empty.
  if (!out.address_line_1 && place?.formattedAddress) {
    out.address_line_1 = place.formattedAddress.split(',')[0];
  }

  return out;
};

/** Human-readable distance: metres under 1 km, otherwise one decimal of a km. */
export const formatDistance = (metres) => {
  if (metres === null || metres === undefined) return null;
  const m = Number(metres);
  if (!Number.isFinite(m)) return null;
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
};
